import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { AdminService, BaseComponent, CommunityChannel, HelperService, Language, LanguageService, LocalizedString, UserGroup, UserService, environment } from "@mypxplat/xplat/core";
import { AppService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { HttpClient } from "@angular/common/http";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { take, takeUntil } from "rxjs/operators";
import { SelectGroupsComponent } from "../select-groups/select-groups.component";

type LangStatus = "idle" | "translating" | "done" | "error";

/**
 * Create / edit a channel on a specific community. Opened from `<myp-community-detail>`.
 * DENG-728 Phase 4 + follow-up.
 *
 * `string_id` is required and immutable after save (posts reference it in their
 * `channels[]` array; renaming would orphan them).
 *
 * `title` is stored as a per-locale map (`{ en, es, fr, ... }`) in DynamoDB so admins
 * can Auto-translate without a code deploy. Admins edit English in the main field,
 * click Auto-translate, review the per-locale values in the collapsed section, and Save.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-add-edit-channel",
  templateUrl: "add-edit-channel.component.html",
  standalone: false,
})
export class AddEditChannelComponent extends BaseComponent implements OnInit {
  /** Owning community — required. Set by the caller before opening the modal. */
  @Input() communityStringId!: string;

  /** When editing an existing channel; undefined for create. */
  @Input() channel: Partial<CommunityChannel> = {};

  /**
   * MySQL groups for the "Restrict post authors" picker. The caller passes the list it
   * already has (community-detail loads it); fetched here only if it arrives empty.
   */
  @Input() groups: UserGroup[] = [];

  @Output() channelSaved: EventEmitter<CommunityChannel> = new EventEmitter();

  /**
   * "Restrict post authors": only members of `postingGroups` may start posts in this
   * channel; replies stay open to everyone. Saved as `posting_groups` (null when off).
   * Turning the switch off keeps the list here, so flipping it back on before saving
   * doesn't lose the selection — but an off switch always saves null.
   */
  public restrictPosting = false;
  public postingGroups: string[] = [];
  public groupsLoading = false;

  public isEdit = false;
  public loading = false;

  /**
   * Working copies of the two translatable fields as locale maps. Kept separate from
   * `channel.title` / `channel.description` (which the payload uses) so ngModel-driven
   * inputs bind cleanly per language without stringifying the object.
   */
  public titles: LocalizedString = { en: "" };
  public descriptions: LocalizedString = { en: "" };

  public otherLanguages: Language[] = [];
  public showAllLanguages = false;
  public langStatus: Record<string, LangStatus> = {};
  public translating = false;
  public missingTranslations = false;

  constructor(
    userService: UserService,
    public activeModal: NgbActiveModal,
    public communityService: WebCommunityService,
    private languageService: LanguageService,
    private http: HttpClient,
    private helperService: HelperService,
    private appService: AppService,
    private adminService: AdminService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.isEdit = !!this.channel?.string_id;
    if (this.channel.is_public === undefined) this.channel.is_public = true;
    if (this.channel.is_alphabetical === undefined) this.channel.is_alphabetical = false;
    if (this.channel.ordering === undefined) this.channel.ordering = 0;

    // Normalize existing (map or bare-string) title/description into working maps.
    this.titles = this.normalize(this.channel.title, this.channel.string_id || "");
    this.descriptions = this.normalize(this.channel.description);

    this.otherLanguages = this.languageService.languages.filter((l) => l.code !== "en");
    this.otherLanguages.forEach((l) => (this.langStatus[l.code] = "idle"));
    this.recomputeMissing();

    this.postingGroups = [...(this.channel.posting_groups || [])];
    this.restrictPosting = this.postingGroups.length > 0;
    if (!this.groups?.length) {
      this.groupsLoading = true;
      this.adminService.getGroups().subscribe({
        next: (result: any) => {
          this.groups = ((result || []) as UserGroup[]).slice().sort((a, b) => (a.name > b.name ? 1 : -1));
          this.groupsLoading = false;
        },
        error: (err) => {
          console.error("[admin/add-edit-channel] getGroups failed", err);
          this.groupsLoading = false;
        },
      });
    }
  }

  toggleRestrictPosting() {
    this.restrictPosting = !this.restrictPosting;
    // Nothing chosen yet — go straight to the picker rather than making them find the button.
    if (this.restrictPosting && !this.postingGroups.length) this.openPostingGroupPicker();
  }

  /** Opens over this modal; its Save replaces the selection, Cancel leaves it alone. */
  openPostingGroupPicker() {
    if (this.groupsLoading) return;
    const modalRef = this.appService.showModal(SelectGroupsComponent, { size: "lg" });
    modalRef.componentInstance.title = "Who can post in this channel?";
    modalRef.componentInstance.description = "Members of any selected group can start posts in this channel. Everyone can still reply. Community moderators and admins can always post.";
    modalRef.componentInstance.groups = this.groups;
    modalRef.componentInstance.selectedNames = [...this.postingGroups];
    modalRef.componentInstance.groupsSelected.pipe(take(1), takeUntil(this.destroy$)).subscribe((names: string[]) => {
      this.postingGroups = names.slice().sort();
    });
  }

  removePostingGroup(name: string) {
    this.postingGroups = this.postingGroups.filter((n) => n !== name);
  }

  private normalize(value: any, fallback = ""): LocalizedString {
    if (!value) return { en: fallback };
    if (typeof value === "string") return { en: value };
    return { ...value };
  }

  recomputeMissing() {
    // Only flag as missing when the English source has content — an empty channel has
    // nothing to translate, and we don't want to nag admins about a blank description.
    const hasEn = !!(this.titles.en || "").trim() || !!(this.descriptions.en || "").trim();
    if (!hasEn) {
      this.missingTranslations = false;
      return;
    }
    this.missingTranslations = this.otherLanguages.some((l) => !this.titles[l.code] && (!!this.titles.en || false));
  }

  autoTranslate() {
    if (!this.channel.string_id) {
      alert("Enter a string ID first — the translate endpoint is keyed by (communityStringId, stringId).");
      return;
    }
    const fields: Record<string, string> = {};
    if ((this.titles.en || "").trim()) fields.title = this.titles.en;
    if ((this.descriptions.en || "").trim()) fields.description = this.descriptions.en;
    if (!Object.keys(fields).length) {
      alert("Enter the English title first, then Auto-translate.");
      return;
    }

    this.translating = true;
    let remaining = this.otherLanguages.length;

    this.otherLanguages.forEach((lang) => {
      this.langStatus[lang.code] = "translating";
      const url = environment.paeApiUrl + `communities/${encodeURIComponent(this.communityStringId)}/channels/${encodeURIComponent(this.channel.string_id!)}/translate`;
      this.http.post<Record<string, Record<string, string>>>(url, JSON.stringify({ fields, targetLang: lang.code }), this.helperService.getHttpOptions()).subscribe({
        next: (result) => {
          if (result?.title?.[lang.code] !== undefined) this.titles = { ...this.titles, [lang.code]: result.title[lang.code] };
          if (result?.description?.[lang.code] !== undefined) this.descriptions = { ...this.descriptions, [lang.code]: result.description[lang.code] };
          this.langStatus[lang.code] = "done";
          if (!--remaining) this.finishTranslate();
        },
        error: () => {
          this.langStatus[lang.code] = "error";
          if (!--remaining) this.finishTranslate();
        },
      });
    });
  }

  private finishTranslate() {
    this.translating = false;
    this.showAllLanguages = true;
    this.recomputeMissing();
  }

  submit() {
    if (!this.communityStringId) {
      alert("communityStringId is missing — cannot save the channel.");
      return;
    }
    if (!this.channel.string_id) {
      alert("string_id is required.");
      return;
    }
    if (!(this.titles.en || "").trim()) {
      alert("English title is required.");
      return;
    }
    if (this.restrictPosting && !this.postingGroups.length) {
      alert("Select at least one group that can post here, or turn off Restrict post authors.");
      return;
    }

    // Drop empty locale entries so we don't persist blank strings that would win over an
    // English fallback at resolve time.
    const cleanMap = (m: LocalizedString): LocalizedString => {
      const out: LocalizedString = {};
      for (const [k, v] of Object.entries(m)) if ((v || "").trim()) out[k] = v;
      return out;
    };

    const payload: Partial<CommunityChannel> = {
      string_id: this.channel.string_id,
      title: cleanMap(this.titles),
      description: (this.descriptions.en || "").trim() ? cleanMap(this.descriptions) : null,
      is_public: !!this.channel.is_public,
      is_alphabetical: !!this.channel.is_alphabetical,
      role: this.channel.role ?? null,
      posting_groups: this.restrictPosting ? this.postingGroups : null,
      ordering: this.channel.ordering ?? 0,
    };

    this.loading = true;
    const request$ = this.isEdit
      ? this.communityService.updateCommunityChannel(this.communityStringId, this.channel.string_id, payload)
      : this.communityService.createCommunityChannel(this.communityStringId, payload);

    request$.subscribe({
      next: (result: any) => {
        this.channelSaved.next(result);
        this.activeModal.close();
      },
      error: (err) => {
        console.error("[admin/add-edit-channel] save failed", err);
        this.loading = false;
        const msg = err?.error?.error || err?.message || "Failed to save channel. See console for details.";
        alert(msg);
      },
    });
  }
}
